import {
  EventRequest,
  createEvent,
  CreateEventOkResponse,
  retrieveEvent,
  RetrieveEventOkResponse,
  partialUpdateEvent,
  PartialUpdateEventOkResponse,
  listEvents,
  PaginatedEventResponseList,
  ListEventsAPIQueryParams,
  createCopyIntegrationTemplateDataToEvent
} from '@goldcast/api/backend';

import { FilterBy, RepeatingEventFilterItemsEnum } from './types';
import { transformServerEvents } from './utils';

import router from '@/router';
import { checkAndShowSalesforceConfig } from '@/studio/customSolutions/DoIt/util';

const sortByMap = {
  0: {
    ordering: 'title'
  },
  1: {
    ordering: '-title'
  },
  2: {
    ordering: 'start_time'
  },
  3: {
    ordering: '-start_time'
  }
};

const eventTypeMap = {
  0: {
    event_type: ['Webinar', 'Conference']
  },
  1: {
    event_type: ['Webinar']
  },
  2: {
    event_type: ['Conference']
  },
  3: {
    event_type: ['Recording']
  }
};

function getFilterByQuery(filterBy: FilterBy) {
  const now = new Date();
  if (filterBy === FilterBy.UpcomingEvents) {
    return {
      end_time_gte: now.toISOString()
    };
  }
  if (filterBy === FilterBy.PastEvents) {
    return {
      end_time_lte: now.toISOString()
    };
  }
  if (filterBy === FilterBy.UpcomingNonLiveEvents) {
    return {
      start_time_gte: now.toISOString()
    };
  }
  if (filterBy === FilterBy.OnDemandEvents) {
    return {
      on_demand_mode: true
    };
  }
  return {};
}

const actions = {
  async loadEvents(
    { dispatch, commit, state, rootGetters, rootState },
    options: {
      seriesId?: string;
      workspaceId?: string;
      isNoWorkspaceOptionSelected?: boolean;
      repeatingEventFilter?: RepeatingEventFilterItemsEnum;
      filterBy?: FilterBy;
    } = {}
  ) {
    const {
      seriesId = '',
      workspaceId = '',
      isNoWorkspaceOptionSelected = false,
      repeatingEventFilter = RepeatingEventFilterItemsEnum.SHOW,
      filterBy: filterByOverride
    } = options;

    const {
      currentPage,
      eventsPerPage,
      sortBy,
      searchTerm,
      filterBy: stateFilterBy,
      selectedTagIds,
      eventTypes
    } = seriesId ? rootState.studioSeriesEventsList : state;

    const filterBy = filterByOverride ?? stateFilterBy;

    const currentOrg = rootGetters['org/getCurrentOrg'];
    const notUseRegistrantsCount = currentOrg?.notUseRegistrantsCount || false;

    if (notUseRegistrantsCount) {
      commit('setEventsPerPage', 10);
    } else {
      commit('setEventsPerPage', 25);
    }

    commit('setIsLoading', true);

    const queryParamsPayload: ListEventsAPIQueryParams = {
      limit: eventsPerPage,
      offset: (currentPage - 1) * eventsPerPage,
      series: seriesId,
      organization: currentOrg?.id,
      tag: selectedTagIds,
      workspace: workspaceId,
      series_type: repeatingEventFilter,
      min: notUseRegistrantsCount ? notUseRegistrantsCount : undefined,
      minimal_data: true,
      ...eventTypeMap[eventTypes],
      ...getFilterByQuery(filterBy),
      ...sortByMap[sortBy]
    };

    if (isNoWorkspaceOptionSelected) {
      queryParamsPayload.workspace__isnull = true;
    }

    return new Promise((resolve, reject) => {
      listEvents({
        queryParams: { ...queryParamsPayload, search: searchTerm },
        queryParamsStringifyOptions: {
          arrayFormat: 'repeat'
        }
      })
        .then((eventsData: PaginatedEventResponseList) => {
          // @ts-ignore
          const events = transformServerEvents(eventsData.results);
          resolve({ events, count: eventsData.count });
        })
        .catch(error => {
          dispatch(
            'studioToast/triggerToast',
            { msg: 'Error Loading Events, Please Try Again', mode: 'error' },
            { root: true }
          );
          reject(error);
        })
        .finally(() => {
          commit('setIsLoading', false);
        });
    });
  },
  async createEvent(_, params: EventRequest) {
    return new Promise((resolve, reject) => {
      createEvent({
        body: params
      })
        .then((event: CreateEventOkResponse) => {
          const { query } = checkAndShowSalesforceConfig();
          resolve({ eventId: event.id, query });
        })
        .catch(reject);
    });
  },
  async loadEventByID(context, { eventId }: { eventId: string }) {
    try {
      context.commit('setIsLoading', true);
      const event: RetrieveEventOkResponse = await retrieveEvent({
        id: eventId,
        queryParams: {
          get_metadata: true
        }
      });
      context.commit('setEventDetails', event);
    } catch (error: any) {
      let errorMessage = 'Error Loading Event, Please Try Again';

      if (error.statusCode === 403) {
        errorMessage = 'You do not have permission to view this event.';
        router.push({ name: 'Events' });
      }

      context.dispatch('studioToast/triggerToast', { msg: errorMessage, mode: 'error' }, { root: true });
    } finally {
      context.commit('setIsLoading', false);
    }
  },

  async updateEvent({ commit, rootState, rootGetters }, payload) {
    commit('setUpdateEventLoading', true);
    const eventId = rootState.studioEventList.currentEventId;

    const currentEvent = rootGetters['studioEventList/getCurrentEvent'];
    const integration_config_updated = currentEvent?.integration_config_updated;

    //Updating the integration_config_updated flag which we call when the integration config is updated
    //this flag is used for repeating event integration field updation
    if (payload && payload.integration_config_updated && integration_config_updated) {
      const integration_config = payload.integration_config_updated;
      payload.integration_config_updated = {
        ...integration_config_updated,
        ...integration_config
      };
    }
    return new Promise((resolve, reject) => {
      partialUpdateEvent({
        id: eventId,
        body: payload
      })
        .then(function (newEventData: PartialUpdateEventOkResponse) {
          // @ts-ignore
          const formattedNewEventData = transformServerEvents([newEventData]);
          commit('updateEvent', formattedNewEventData);
          resolve(formattedNewEventData);
        })
        .catch(reject)
        .finally(() => {
          commit('setUpdateEventLoading', false);
        });
    });
  },

  async fetchEventMembers(
    context,
    {
      eventId,
      offset = 0,
      searchText,
      sortByColumn
    }: {
      eventId: string;
      offset?: number;
      searchText?: string;
      sortByColumn?: string;
    }
  ) {
    if (eventId) {
      const params = {
        ...(offset && { offset }),
        ...(searchText && searchText !== '' && { search: searchText }),
        ...(sortByColumn && sortByColumn !== '' && { ordering: sortByColumn }),
        limit: 20
      };
      return context.dispatch(
        'eventList/getEventMembers',
        {
          eventID: eventId,
          params
        },
        { root: true }
      );
    }
  },
  async copyIntegrationTemplateDataToEvent(context, eventId: string) {
    context.commit('setIsIntegrationTemplateDataLoadingAndUpdatingEvent', true);
    try {
      const updatedIntegrationData = await createCopyIntegrationTemplateDataToEvent({
        id: eventId
      });
      context.commit('partialUpdateEvent', { eventId, partialEvent: updatedIntegrationData });
    } catch (error: any) {
      const errorMessage = error?.message || 'failed to load and update integration template data';
      context.dispatch('studioToast/triggerToast', { msg: errorMessage, mode: 'error' }, { root: true });
    } finally {
      context.commit('setIsIntegrationTemplateDataLoadingAndUpdatingEvent', false);
    }
  }
};

export default actions;
